import { useEffect, useRef } from "react";

interface UseTitleNotificationOptions {
  prefix?: string;
  showCount?: boolean;
  blinkInterval?: number;
  isEnabled?: boolean;
}

export function useTitleNotification({
  prefix = "🔔 ",
  showCount = true,
  blinkInterval = 1000,
  isEnabled = true,
}: UseTitleNotificationOptions = {}) {
  const originalTitle = useRef<string>(document.title);
  const intervalRef = useRef<number | null>(null);
  const isBlinking = useRef<boolean>(false);
  const count = useRef<number>(0);
  const enabledRef = useRef<boolean>(isEnabled);

  // Update enabled state when the prop changes
  useEffect(() => {
    enabledRef.current = isEnabled;
    if (!isEnabled && isBlinking.current) {
      stopBlinking();
    } else if (isEnabled && count.current > 0 && !isBlinking.current) {
      startBlinking();
    }
  }, [isEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        document.title = originalTitle.current;
      }
    };
  }, []);

  const updateCount = (newCount: number) => {
    count.current = newCount;

    if (newCount > 0 && enabledRef.current) {
      startBlinking();
    } else {
      stopBlinking();
    }
  };

  const startBlinking = () => {
    if (isBlinking.current) return;

    isBlinking.current = true;
    let isOriginal = false;

    // Store original title when starting to blink
    originalTitle.current = document.title;

    // Clear existing interval if any
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }

    // Start interval to alternate between titles
    intervalRef.current = window.setInterval(() => {
      if (isOriginal) {
        document.title = originalTitle.current;
      } else {
        const countText = showCount && count.current > 0 ? `(${count.current}) ` : "";
        document.title = `${prefix}${countText}${originalTitle.current}`;
      }
      isOriginal = !isOriginal;
    }, blinkInterval);
  };

  const stopBlinking = () => {
    if (!isBlinking.current) return;

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset title
    document.title = originalTitle.current;
    isBlinking.current = false;
  };

  const setEnabled = (enabled: boolean) => {
    enabledRef.current = enabled;
    if (!enabled && isBlinking.current) {
      stopBlinking();
    } else if (enabled && count.current > 0 && !isBlinking.current) {
      startBlinking();
    }
  };

  return { updateCount, stopBlinking, setEnabled };
}
